import React, { useEffect, useState } from 'react';
import api from '../services/ApiService';

type LiveCall = {
  callId: string;
  tenantId: number | null;
  tenantName: string | null;
  extNum: string | null;
  display: string | null;
  direction: string;
  number: string | null;
  trunk: string | null;
  durationSec: number;
  state: string;
  flags: string[];
  legCount: number;
};

type Snapshot = {
  generatedAt: string;
  scope: string;
  totalCalls: number;
  fraudCount: number;
  calls: LiveCall[];
};

const FLAG_LABEL: Record<string, string> = {
  unauthorized_outbound: 'Unauthorized outbound — no known extension',
  unknown_endpoint: 'Unknown SIP endpoint',
};

function fmtDur(s: number) {
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${m}:${String(ss).padStart(2, '0')}`;
}

export default function LiveCalls() {
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [fraudOnly, setFraudOnly] = useState(false);

  const load = async () => {
    try {
      const data = await api.getLiveCalls();
      setSnap(data);
      setErr('');
    } catch (e: any) {
      setErr(e?.response?.data?.error || 'Failed to load live calls');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, []);

  const all = snap?.calls || [];
  const outbound = all.filter((c) => c.direction === 'outbound').length;
  const inbound = all.filter((c) => c.direction !== 'outbound' && c.direction !== 'internal').length;
  const fraud = all.filter((c) => c.flags.length > 0).length;
  const calls = fraudOnly ? all.filter((c) => c.flags.length > 0) : all;

  return (
    <>
      <div className="admin-page-title">Live Calls</div>
      <div className="admin-page-subtitle">
        Real-time calls read directly from the SIP layer (Asterisk) for fraud &amp; abuse monitoring.
        {snap?.scope === 'all' ? ' Showing all tenants.' : ' Showing your tenant.'}
      </div>

      <div className="dashboard-grid">
        <StatCard title="Live Calls" value={all.length} />
        <StatCard title="Outbound" value={outbound} />
        <StatCard title="Inbound" value={inbound} />
        <StatCard title="Fraud Alerts" value={fraud} danger={fraud > 0} />
      </div>

      <div className="content-card">
        <div className="admin-card-header">
          <h3>
            Active Calls
            {snap ? `  ·  updated ${new Date(snap.generatedAt).toLocaleTimeString()}` : ''}
          </h3>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <label className="admin-muted" style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" checked={fraudOnly} onChange={(e) => setFraudOnly(e.target.checked)} />
              Fraud only
            </label>
            <button className="secondary-btn" onClick={load}>Refresh</button>
          </div>
        </div>

        {err && <p className="admin-muted" style={{ color: '#dc2626' }}>{err}</p>}
        {loading && !snap && <p className="admin-muted">Loading…</p>}
        {snap && calls.length === 0 && (
          <p className="admin-muted">{fraudOnly ? 'No fraud alerts right now.' : 'No active calls right now.'}</p>
        )}

        {calls.length > 0 && (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Extension</th>
                <th>Direction</th>
                <th>Number</th>
                <th>Gateway / Trunk</th>
                <th>Duration</th>
                <th>State</th>
                <th>Fraud</th>
              </tr>
            </thead>
            <tbody>
              {calls.map((c) => (
                <tr key={c.callId} style={c.flags.length ? { background: '#fef2f2' } : undefined}>
                  <td>
                    {c.tenantName || (c.tenantId != null ? `Tenant ${c.tenantId}` : <span className="admin-muted">—</span>)}
                  </td>
                  <td>
                    {c.extNum ? (
                      <>
                        <strong>{c.extNum}</strong>
                        {c.display ? <div className="admin-muted">{c.display}</div> : null}
                      </>
                    ) : (
                      <span className="admin-muted">unknown</span>
                    )}
                  </td>
                  <td>
                    <span className={`admin-pill ${c.direction === 'outbound' ? 'green' : ''}`}>{c.direction}</span>
                  </td>
                  <td>{c.number || '—'}</td>
                  <td>{c.trunk || <span className="admin-muted">—</span>}</td>
                  <td>{fmtDur(c.durationSec)}</td>
                  <td>{c.state}</td>
                  <td>
                    {c.flags.length === 0 ? (
                      <span className="admin-pill green">OK</span>
                    ) : (
                      c.flags.map((f) => (
                        <span key={f} className="admin-pill red" title={FLAG_LABEL[f] || f} style={{ marginRight: 4 }}>
                          {FLAG_LABEL[f] || f}
                        </span>
                      ))
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function StatCard({ title, value, danger }: { title: string; value: number; danger?: boolean }) {
  return (
    <div className="stat-card">
      <div className="stat-title">{title}</div>
      <div className="stat-value" style={danger ? { color: '#dc2626' } : undefined}>{value}</div>
    </div>
  );
}
